import {Injectable} from '@angular/core';
import {NavigationEnd, Router, ActivatedRoute, Params, ActivatedRouteSnapshot} from '@angular/router';
import {filter, withLatestFrom} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';
import {BreadCrumbsObj} from '../shared/common-components/common-structure';
import * as _ from 'lodash';
import AppConstants, {
  agency_auditor_role,
  agency_admin_role,
  agency_user,
  carrier_user,
  carrier_user_admin,
  carrier_user_RF,
  system_admin
} from '../shared/common-components/AppConstants';
import {User} from './user/user';

@Injectable({
  providedIn: 'root'
})
export class RoutingStateService {

  private breadCrumbItems = new BehaviorSubject<BreadCrumbItem[]>([]);
  breadCrumbs$ = this.breadCrumbItems.asObservable();
  homeRoute: ActivatedRoute;
  breadCrumbsTitle: string;
  currentUser: User;
  userPermission: Number;


  constructor(private activatedRoute: ActivatedRoute, private router: Router) {
    this.homeRoute = activatedRoute;
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), withLatestFrom(this.breadCrumbs$))
      .subscribe(([e, breadCrumbItems]) => {
        window.scrollTo(0, 0);
        this.modifyBreadCrumbItems(
          this.activatedRoute.root.children[0].snapshot, breadCrumbItems
        );
      });
  }

  // Modify the bread crumb list based on current route snapshot and existing breadcrumbs
  modifyBreadCrumbItems(snapshot: ActivatedRouteSnapshot, breadCrumbItems: BreadCrumbItem[]) {
    if (
      snapshot.routeConfig.data &&
      snapshot.routeConfig.data.isBreadCrumbNotApplicable
    ) {
      if (snapshot.routeConfig.path === 'landing') {
        this.breadCrumbItems.next([breadCrumbItems.pop()]);
        breadCrumbItems.push(<BreadCrumbItem>{
          title: BreadCrumbsObj[snapshot.routeConfig.path],
          url: snapshot.routeConfig.path,
          params: snapshot.queryParams,
          isActive: true
        });
        this.breadCrumbItems.next(breadCrumbItems);
      } else {
        return;
      }
    }
    const breadCrumbIndexForCurrentRoute = breadCrumbItems.findIndex(
      bc => bc.url.toUpperCase() === snapshot.routeConfig.path.toUpperCase());
    if (breadCrumbIndexForCurrentRoute > -1) {
      breadCrumbItems = breadCrumbItems.slice(0, breadCrumbIndexForCurrentRoute);
    }
    breadCrumbItems.forEach(bc => (bc.isActive = false));
    this.currentUser = (AppConstants.isNotNullCondition(localStorage.currentUser))? JSON.parse(localStorage.currentUser) : null;
    if (this.currentUser) {
      const roleName = this.currentUser.roles[0].role;
      this.userPermission = 0;
      if (this.checkUserRole(this.currentUser, system_admin)) {
        this.userPermission = 1;
      } else if (this.checkUserRole(this.currentUser, agency_admin_role) ||
        this.checkUserRole(this.currentUser, agency_user) ||
        this.checkUserRole(this.currentUser, agency_auditor_role)) {
        this.userPermission = 2;
      } else if (this.checkUserRole(this.currentUser, carrier_user) ||
        this.checkUserRole(this.currentUser, carrier_user_admin) ||
        this.checkUserRole(this.currentUser, carrier_user_RF)) {
        this.userPermission = 3;
      }
      this.breadCrumbsTitle = BreadCrumbsObj[snapshot.routeConfig.path];
      switch (this.userPermission) {
        case 2:
          if (BreadCrumbsObj[snapshot.routeConfig.path] === 'HHG Comprehensive Shipment Reports') {
            this.breadCrumbsTitle = 'HHG Shipment Reports';
          } else if (BreadCrumbsObj[snapshot.routeConfig.path] === 'XTOS Shipment Reports') {
            this.breadCrumbsTitle = 'XTOS Shipment Reports';
          } else if (BreadCrumbsObj[snapshot.routeConfig.path] === 'SIT Comprehensive Shipment Reports') {
            this.breadCrumbsTitle = 'SIT Shipment Reports';
          }
          break;
        case 3:
          if (BreadCrumbsObj[snapshot.routeConfig.path] === 'DUNS') {
            if (snapshot.queryParams.rateType === 'HHG') {
              this.breadCrumbsTitle = 'HHG Rate Filing';
            } else if (snapshot.queryParams.rateType === 'FRT') {
              this.breadCrumbsTitle = 'FRT Rate Filing';
            }
          } else if (BreadCrumbsObj[snapshot.routeConfig.path] === 'XTOS Shipment Reports') {
            this.breadCrumbsTitle = 'XTOS Shipment Reporting';
          } else if (BreadCrumbsObj[snapshot.routeConfig.path] === 'HHG Comprehensive Shipment Reports') {
            this.breadCrumbsTitle = 'HHG Shipment Reports';
          } else if (BreadCrumbsObj[snapshot.routeConfig.path] === 'SIT Comprehensive Shipment Reports') {
            this.breadCrumbsTitle = 'SIT Shipment Reports';
          }
          break;
        default:
          break;
      }
    }
    if(this.currentUser){
      if (snapshot.routeConfig.path === 'iff-upload'){
        if (this.currentUser.permissions === 'FRT'){
          this.breadCrumbsTitle = 'Upload Shipment Reports';
        }else{
          this.breadCrumbsTitle = 'Upload HHG/SIT Shipment Reports';
        }
      }
    }

    breadCrumbItems.push(<BreadCrumbItem>{
      title: this.breadCrumbsTitle,
      url: snapshot.routeConfig.path,
      params: snapshot.queryParams,
      isActive: true
    });
    this.breadCrumbItems.next(breadCrumbItems);
  }

  // Clear all bread crumb list expect for the current routing
  clearBreadCrumbs() {
    if (this.homeRoute.root?.children[0]?.snapshot?.routeConfig?.path === 'landing') {
      const landingPage: BreadCrumbItem = {
        title: BreadCrumbsObj[this.homeRoute.root.children[0].snapshot.routeConfig.path],
        url: this.homeRoute.root.children[0].snapshot.routeConfig.path,
        params: this.homeRoute.root.children[0].snapshot.queryParams,
        isActive: true
      };
      this.breadCrumbItems.next([landingPage]);
    } else {
      const breadCrumbs = this.breadCrumbItems.value;
      if (breadCrumbs && breadCrumbs.length > 1) {
        this.breadCrumbItems.next([breadCrumbs.pop()]);
      }
    }
  }

  addCrumbsFromTabs(tabName: any) {
    const breadCrumbs = this.breadCrumbItems.value;
    if (breadCrumbs && breadCrumbs.length > 1) {
      this.breadCrumbItems.next([breadCrumbs.pop()]);
    }
    const landingPage: BreadCrumbItem = {
      title: BreadCrumbsObj[tabName],
      url: this.homeRoute.root.children[0].snapshot.routeConfig.path,
      params: this.homeRoute.root.children[0].snapshot.queryParams,
      isActive: true
    };
    breadCrumbs.push(landingPage);
    this.breadCrumbItems.next(breadCrumbs);
  }

  emptyBreadCrumbs() {
    const breadCrumbs = this.breadCrumbItems?.value;
    if (breadCrumbs && breadCrumbs.length > 0) {
      this.breadCrumbItems.next([breadCrumbs.pop()]);
    }
  }

  checkUserRole(currentUser, roles?) {
    if (currentUser) {
      const userRoles = _.map(currentUser.roles, x => x.role);
      if (userRoles && userRoles.length > 0) {
        return _.includes(userRoles, roles);
      }
    }
  }


}

export class BreadCrumbItem {
  title: string;
  url: string;
  params: Params;
  isActive = false;
}
